import { RecurringFrequency } from "./flow-instances";

/**
 * Recurring deposit schedule arithmetic (QL-282, "drip feed").
 *
 * The advisor may enter a recurring plan either as an amount per occurrence or
 * as a total sum spread over the plan's period. Only the per-occurrence amount
 * is ever persisted (`goal_portfolio.recurringAmount`); the total is an input
 * aid that is divided by the deposit count before save, so every downstream
 * rule — min-buy, forecast, cost — keeps working on the per-event amount.
 *
 * The count convention here mirrors `DepositBucket` in the API's cost
 * simulation, which is what actually pays the deposits out: the first deposit
 * lands in the start month, the next every `MONTHS_PER_DEPOSIT[frequency]`
 * months, and deposits continue while the month is at or before the end month.
 * A quarterly plan from 2026-09 to 2027-09 therefore makes 5 deposits, not 4.
 */

/** Months between two consecutive deposits, per frequency. */
export const MONTHS_PER_DEPOSIT: Record<RecurringFrequency, number> = {
  [RecurringFrequency.MONTHLY]: 1,
  [RecurringFrequency.QUARTERLY]: 3,
  [RecurringFrequency.YEARLY]: 12,
};

/** Deposits a year by frequency (12 / 4 / 1) — for annualising a per-deposit amount. */
export const PAYMENTS_PER_YEAR: Record<RecurringFrequency, number> = {
  [RecurringFrequency.MONTHLY]: 12 / MONTHS_PER_DEPOSIT[RecurringFrequency.MONTHLY],
  [RecurringFrequency.QUARTERLY]: 12 / MONTHS_PER_DEPOSIT[RecurringFrequency.QUARTERLY],
  [RecurringFrequency.YEARLY]: 12 / MONTHS_PER_DEPOSIT[RecurringFrequency.YEARLY],
};

/**
 * Whether a stored or submitted value is one of the known frequencies. The
 * column is a bare varchar and every reader falls back to monthly on a value it
 * does not recognise, so writes check with this first.
 */
export function isRecurringFrequency(value: unknown): value is RecurringFrequency {
  return (Object.values(RecurringFrequency) as unknown[]).includes(value);
}

/**
 * Pick the value for a frequency, e.g. the "/quarter" label. Typing the map as a
 * `Record` makes a new frequency a build error at every call site. A value that
 * is not a known frequency gets the monthly entry, as the deposit count and the
 * cost simulation treat it.
 */
export function byFrequency<T>(
  frequency: string | null | undefined,
  values: Record<RecurringFrequency, T>,
): T {
  return isRecurringFrequency(frequency) ? values[frequency] : values[RecurringFrequency.MONTHLY];
}

/** Parse a "YYYY-MM" month into a month index, or null when it is not one. */
function parseYearMonth(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return year * 12 + (month - 1);
}

/**
 * How many deposits a recurring plan makes between two "YYYY-MM" months,
 * inclusive of the end month when a deposit falls in it.
 *
 * Returns null when either month is missing or malformed, or when the end month
 * precedes the start month — the caller has no valid period to divide over and
 * must fall back to the per-occurrence amount. A start month equal to the end
 * month is a valid one-deposit period.
 */
export function countDeposits(
  startYearMonth: string | null | undefined,
  endYearMonth: string | null | undefined,
  frequency: RecurringFrequency | null | undefined,
): number | null {
  const start = parseYearMonth(startYearMonth);
  const end = parseYearMonth(endYearMonth);
  if (start === null || end === null || end < start) return null;
  const step = MONTHS_PER_DEPOSIT[frequency ?? RecurringFrequency.MONTHLY] ?? 1;
  return Math.floor((end - start) / step) + 1;
}

/**
 * An amount as whole cents, the two-decimal precision it is entered and stored
 * at. Splitting and recombining go through cents because doing the arithmetic
 * on the decimal value first loses half-cents to binary rounding: `0.29 / 2`
 * scales to 14.499999999999998, which would round down to 0.14 rather than up
 * to 0.15.
 */
function toCents(value: number): number {
  return Math.round(value * 100);
}

/**
 * Split a total sum into equal per-occurrence deposits.
 *
 * Every deposit is the same rounded amount — there is no remainder payment — so
 * `perDeposit × count` can differ from the entered total by a few cents.
 * Returns null when the total or the count is unusable.
 */
export function perDepositFromTotal(
  totalAmount: number | null | undefined,
  depositCount: number | null | undefined,
): number | null {
  if (totalAmount == null || !Number.isFinite(totalAmount)) return null;
  if (depositCount == null || !(depositCount > 0)) return null;
  return Math.round(toCents(totalAmount) / depositCount) / 100;
}

/** The total a plan pays out: the per-occurrence amount times the deposit count. */
export function totalFromPerDeposit(
  perDepositAmount: number | null | undefined,
  depositCount: number | null | undefined,
): number | null {
  if (perDepositAmount == null || !Number.isFinite(perDepositAmount)) return null;
  if (depositCount == null || !(depositCount > 0)) return null;
  return (toCents(perDepositAmount) * depositCount) / 100;
}
